import { useEffect, useState } from "react";
import { Outlet, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { Send } from "lucide-react";
import {
  Page,
  Table,
  type Column,
  Autocomplete,
  Badge,
  Button,
  BulkActionBar,
  Input,
  Select,
  Combobox,
  FilterField,
  FilterPanel,
  DateRangePicker,
  type DateRangeValue,
  Pagination,
  Text,
  EmptyState,
} from "@marekl/adminer/components";
import { useDebouncedSearchFilter, useRowSelection } from "@marekl/adminer/hooks";
import { notifySuccess, notifyWarning } from "@marekl/adminer/api";
import { dayjs, toApiDate, type Dayjs } from "@marekl/adminer/datetime";
import { formatDate, formatTime } from "@marekl/adminer/format";
import { useIsAdmin } from "../../auth/useAuth";
import {
  fetchRecipientSuggestions,
  isMessageListLive,
  useBulkRetryMessages,
  useMessageList,
  type Message,
} from "../../lib/queries/messages";
import { useSenderList, type Sender } from "../../lib/queries/senders";
import { usePagedList } from "../../lib/usePagedList";
import { SenderChip } from "../../components/SenderChip";
import { RefreshButton } from "../../components/RefreshButton";
import { MessageStateBadge } from "./MessageStateBadge";
import { STATE_LABELS, labelCategory } from "./messageConstants";
import { ComposeMessageModal } from "./ComposeMessageModal";

const STATE_OPTIONS = [
  { value: "", label: "All" },
  ...Object.entries(STATE_LABELS).map(([value, label]) => ({ value, label })),
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All" },
  { value: "transactional", label: "Transactional" },
  { value: "marketing", label: "Marketing" },
];

/** Most frequent `reason` among the bulk-retry skips. */
function dominantSkipReason(skipped: { id: string; reason: string }[]): string {
  const counts = new Map<string, number>();
  for (const s of skipped) counts.set(s.reason, (counts.get(s.reason) ?? 0) + 1);
  let best = skipped[0].reason;
  let max = 0;
  for (const [reason, count] of counts) {
    if (count > max) {
      best = reason;
      max = count;
    }
  }
  return best;
}

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["messages"], ["senders"]];

export function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useIsAdmin();
  const bulkRetry = useBulkRetryMessages();
  const navigate = useNavigate();
  const [composeOpen, setComposeOpen] = useState(false);
  // The detail route is a child of this one, so the id lives on the match
  // rather than on this component's own params.
  const openMessageId = useMatch("/messages/:id")?.params.id;
  // The mark outlives the dialog: while the dialog is open the row is behind a
  // backdrop and nobody can see it, and the moment it closes is exactly when
  // the mark earns its keep — it says where you were in a page of near
  // identical rows. It is cleared by leaving the list, not by closing.
  const [lastViewedId, setLastViewedId] = useState<string | undefined>();
  useEffect(() => {
    if (openMessageId) setLastViewedId(openMessageId);
  }, [openMessageId]);
  const markedId = openMessageId ?? lastViewedId;

  const state = searchParams.get("state") ?? "";
  const category = searchParams.get("category") ?? "";
  const sender = searchParams.get("sender") ?? "";
  const batchId = searchParams.get("batch_id") ?? "";
  const q = searchParams.get("q") ?? "";
  const reference = searchParams.get("reference") ?? "";
  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  // Read only so the poll can stand down off the first page; the cursor
  // itself stays the lib pagination hook's business.
  const cursor = searchParams.get("cursor") ?? "";

  const fromDate: Dayjs | null = from ? dayjs(from, "YYYY-MM-DD", true) : null;
  const toDate: Dayjs | null = to ? dayjs(to, "YYYY-MM-DD", true) : null;
  const dateRange: DateRangeValue | null =
    fromDate?.isValid() && toDate?.isValid()
      ? { from: fromDate, to: toDate }
      : null;

  const senders = useSenderList();

  const { data, isLoading, error, refetch, pagination, filterReset, summary } =
    usePagedList(
      useMessageList,
      {
        state: state || undefined,
        category: category || undefined,
        sender: sender || undefined,
        batch_id: batchId || undefined,
        q: q || undefined,
        reference: reference || undefined,
        // Wire format is date-time; expand the picked days to full-day bounds.
        from: fromDate?.isValid() ? fromDate.startOf("day").toISOString() : undefined,
        to: toDate?.isValid() ? toDate.endOf("day").toISOString() : undefined,
      },
      {
        searchParams,
        setSearchParams,
        nounForms: ["message", "messages", "messages"],
      },
    );

  const items = data?.items ?? [];
  const live = isMessageListLive(
    { state: state || undefined, cursor: cursor || undefined },
    items,
  );

  // Selection is deliberately not pruned by the hook on refetches; clear it
  // explicitly after the bulk mutation and on filter changes (below).
  const sel = useRowSelection(items, (r) => r.id);

  function applyFilter(key: string, value: string) {
    sel.clear();
    filterReset((p) => {
      if (value) {
        p.set(key, value);
      } else {
        p.delete(key);
      }
    });
  }

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => applyFilter("q", next),
  });

  // The reference filter matches exactly, so it commits the trimmed value.
  const { draft: referenceDraft, setDraft: setReferenceDraft } = useDebouncedSearchFilter({
    value: reference,
    onCommit: (next) => applyFilter("reference", next.trim()),
  });

  function setDateRange(next: DateRangeValue | null) {
    sel.clear();
    filterReset((p) => {
      const nextFrom = next ? toApiDate(next.from) : null;
      const nextTo = next ? toApiDate(next.to) : null;
      if (nextFrom) p.set("from", nextFrom);
      else p.delete("from");
      if (nextTo) p.set("to", nextTo);
      else p.delete("to");
    });
  }

  /**
   * Bulk retry over the whole selection (off-page keys included). Any row can
   * be selected — the endpoint skips non-retryable messages and reports each
   * skip with a reason, which is surfaced as a warning toast.
   */
  async function retrySelected() {
    const ids = [...sel.selectedKeys].map(String);
    try {
      const res = await bulkRetry.mutateAsync({ ids });
      const skipped = res.skipped ?? [];
      notifySuccess(
        `${res.retried} ${res.retried === 1 ? "message" : "messages"} queued for retry.`,
      );
      if (skipped.length > 0) {
        notifyWarning(
          `Skipped ${skipped.length} ${skipped.length === 1 ? "message" : "messages"} — most common reason: ${dominantSkipReason(skipped)}.`,
        );
      }
      sel.clear();
    } catch {
      // global mutation error toast already shown
    }
  }

  // Row links carry the current filters so the list underneath the detail
  // route modal stays filtered and closing restores the same URL.
  const detailSearch = searchParams.toString();
  const detailHref = (id: string) =>
    `/messages/${id}${detailSearch ? `?${detailSearch}` : ""}`;

  const columns: Column<Message>[] = [
    {
      key: "created_at",
      header: "Time",
      tdClass: "lm-cell-time",
      // Two lines: the date, then the time under it. A queue is scanned down
      // the column, and the date repeats for every row of the same day.
      render: (row) => (
        <>
          {formatDate(row.created_at)}
          <span className="lm-time">{formatTime(row.created_at)}</span>
        </>
      ),
    },
    {
      key: "to",
      header: "Recipient",
      primary: true,
      tdClass: "lm-cell-recipient",
      // Real link to the nested detail route, so ⌘-click / middle-click work.
      linkTo: (row) => detailHref(row.id),
      render: (row) => row.to_address,
    },
    {
      key: "subject",
      header: "Subject",
      // An SMS has no subject; show what it is instead of an empty cell.
      render: (row) =>
        row.channel === "sms" ? (
          <Badge variant="muted">SMS</Badge>
        ) : row.subject ? (
          <span className="lm-subject">{row.subject}</span>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "sender",
      header: "Sender",
      render: (row) => <SenderChip senderKey={row.sender_key} />,
    },
    {
      key: "category",
      header: "Category",
      render: (row) => labelCategory(row.category),
    },
    {
      key: "state",
      header: "State",
      render: (row) => <MessageStateBadge state={row.state} />,
    },
    {
      key: "attempts",
      header: "Attempts",
      numeric: true,
      render: (row) => <Text variant="mono">{row.attempts}</Text>,
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action to={detailHref(row.id)}>Details →</Table.Action>
        </Table.Actions>
      ),
    },
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="Messages"
          subtitle="the outbound message queue across channels"
          subtitleVariant="tagline"
          actions={
            <>
              <RefreshButton queryKeys={REFRESH_KEYS} live={live} />
              {isAdmin && (
                <Button variant="primary" onClick={() => setComposeOpen(true)}>
                  <Send size={16} aria-hidden="true" />
                  New message
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <FilterPanel>
            <FilterField>
              <Select
                label="State"
                value={state}
                onChange={(e) => applyFilter("state", e.target.value)}
              >
                {STATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField>
              <Select
                label="Category"
                value={category}
                onChange={(e) => applyFilter("category", e.target.value)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField>
              <Combobox<Sender, string>
                label="Sender"
                options={senders.data ?? []}
                getOptionLabel={(s) => s.name}
                getOptionValue={(s) => s.key}
                getOptionKey={(s) => s.id}
                value={sender || null}
                onChange={(v) => applyFilter("sender", v ?? "")}
              />
            </FilterField>
            <FilterField>
              <DateRangePicker
                label="Date range"
                value={dateRange}
                onChange={setDateRange}
                maxDate={dayjs()}
              />
            </FilterField>
            <FilterField>
              <Input
                label="Reference"
                value={referenceDraft}
                onChange={(e) => setReferenceDraft(e.target.value)}
                placeholder="exact client reference"
              />
            </FilterField>
            <FilterField wide>
              {/* Text-first search with async recipient suggestions; free text
                  still commits via the debounced filter (subjects included). */}
              <Autocomplete<string>
                label="Search"
                value={qDraft}
                onChange={setQDraft}
                loadSuggestions={fetchRecipientSuggestions}
                getSuggestionKey={(s) => s}
                onSelectSuggestion={(address) => {
                  setQDraft(address);
                  applyFilter("q", address);
                }}
                placeholder="address or subject"
              />
            </FilterField>
          </FilterPanel>

          {batchId && (
            <Page.SectionHeader
              title={`Batch ${batchId}`}
              meta={
                <Button size="sm" variant="primary" weight="outline" onClick={() => applyFilter("batch_id", "")}>
                  Clear batch filter
                </Button>
              }
            />
          )}

          <Table<Message>
            striped
            cards
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            // The row whose detail is open stays marked underneath the dialog,
            // so closing it returns you to a visible place in the list.
            rowClassName={(r) => (r.id === markedId ? "lm-row--active" : undefined)}
            // Bulk retry is an admin call; a viewer gets no selection column.
            selection={
              isAdmin
                ? { ...sel.selection, label: (r) => r.to_address }
                : undefined
            }
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No messages"
                description="No messages match the selected filters."
              />
            }
          />
          {isAdmin && (
            <BulkActionBar
              count={sel.selectedCount}
              onClear={sel.clear}
              nounForms={["message", "messages", "messages"]}
            >
              <Button
                size="sm"
                variant="primary"
                loading={bulkRetry.isPending}
                onClick={() => void retrySelected()}
              >
                Retry selected
              </Button>
            </BulkActionBar>
          )}
        </Page.Content>
        <Page.Footer>
          <Pagination {...pagination} summary={summary} />
        </Page.Footer>
      </Page>

      {composeOpen && (
        <ComposeMessageModal
          onClose={() => setComposeOpen(false)}
          onSent={(id) => {
            setComposeOpen(false);
            // Land on the new message so its state (sent / failed) is visible.
            navigate(detailHref(id));
          }}
        />
      )}

      {/* The /messages/:id detail route modal renders here, over the list. */}
      <Outlet />
    </>
  );
}
